import React from 'react';
import { AppBar, Toolbar, Box, Button, Typography } from '@mui/material';
import LogoutIcon from '@mui/icons-material/LogoutRounded';
import { Outlet, NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { UserRole } from '@lysi-web/shared-types';
import { useAuth } from '../../contexts/AuthContext';

export const PharmacyLayout: React.FC = () => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background:
          'radial-gradient(ellipse 80% 50% at 50% 0%, #D6DFEE 0%, #EEF2F8 55%, #E8EDF5 100%)',
      }}
    >
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: 'rgba(255,255,255,0.82)',
          backdropFilter: 'saturate(140%) blur(14px)',
          color: 'text.headline',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 64, sm: 72 }, gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            <Typography
              sx={{
                fontFamily: '"Bricolage Grotesque", "Plus Jakarta Sans", sans-serif',
                fontWeight: 500,
                fontSize: { xs: '1.4rem', sm: '1.65rem' },
                letterSpacing: '-0.025em',
                color: 'text.headline',
                lineHeight: 1,
              }}
            >
              Lysi
            </Typography>
            <Box
              sx={{
                display: { xs: 'none', sm: 'inline-block' },
                width: '1px',
                height: 18,
                bgcolor: 'divider',
              }}
            />
            <Typography
              sx={{
                display: { xs: 'none', sm: 'inline' },
                fontFamily: '"Plus Jakarta Sans", sans-serif',
                fontSize: '0.7rem',
                fontWeight: 600,
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                color: 'primary.main',
              }}
            >
              {t('kanban.pharmacyBadge')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5, mr: 'auto' }}>
            <Button
              component={NavLink}
              to="/pharmacy/orders"
              size="small"
              sx={{
                color: 'text.secondary',
                fontWeight: 600,
                '&.active': { color: 'text.headline', bgcolor: 'primary.softBg' },
              }}
            >
              {t('reports.navOrders')}
            </Button>
            {user?.role === UserRole.PHARMACY_MANAGER && (
              <Button
                component={NavLink}
                to="/pharmacy/reports"
                size="small"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 600,
                  '&.active': { color: 'text.headline', bgcolor: 'primary.softBg' },
                }}
              >
                {t('reports.navReports')}
              </Button>
            )}
          </Box>
          <Typography
            variant="body2"
            sx={{
              display: { xs: 'none', md: 'block' },
              color: 'text.secondary',
              fontVariant: 'tabular-nums',
            }}
          >
            {user?.email}
          </Typography>
          <Button
            onClick={logout}
            variant="text"
            size="small"
            startIcon={<LogoutIcon sx={{ fontSize: 18 }} />}
            sx={{
              color: 'text.secondary',
              fontWeight: 500,
              minHeight: 36,
              px: 1.5,
              '&:hover': { color: 'text.headline', bgcolor: 'transparent' },
            }}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
              {t('kanban.logout')}
            </Box>
          </Button>
        </Toolbar>
      </AppBar>
      <Box component="main" sx={{ flex: 1 }}>
        <Outlet />
      </Box>
    </Box>
  );
};
